import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { orDash } from '@/lib/format';
import {
  XERO_ENTITIES,
  bandLabel,
  bandTone,
  compareMatches,
  confidenceBand,
  isForkingLink,
  type XeroContactMatch,
  type XeroEntity,
} from '@/lib/xero';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  Skeleton,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

/**
 * Attaching an EMERGE record to a Xero contact that already exists.
 *
 * ~92% of the tenant's ~938 contacts are expense vendors, not customers, so a
 * picker ordered by string similarity alone would happily offer a hotel above
 * an invoiced university — order comes from `compareMatches` alone, and every
 * non-customer carries a visible marker. Linking is always a human decision:
 * the backend scores, this dialog never auto-picks a "best match".
 */

/**
 * Enough candidates that a genuine match is on screen, few enough that the list
 * is still readable at 320px. Beyond about this many the operator is scanning,
 * not choosing, and the answer is a better search term.
 */
const RESULT_LIMIT = 20;

/** Fewer than a full page — enough to read as a list, not a wall of shimmer. */
const SKELETON_ROWS = 4;

export interface XeroLinkModalProps {
  open: boolean;
  onClose: () => void;
  entity: XeroEntity;
  recordId: number;
  recordName: string;
  /** Called after a successful link so the parent refetches. */
  onLinked: () => void;
}

/**
 * Unmounted while closed rather than held open={false}, so every piece of state
 * below is seeded from props on mount and there is no reset effect to forget a
 * field in. Reopening the dialog is a fresh mount, which is what an operator
 * expects after a mis-click on the wrong candidate.
 */
export function XeroLinkModal({ open, ...rest }: XeroLinkModalProps) {
  if (!open) return null;
  return <XeroLinkPicker {...rest} />;
}

function XeroLinkPicker({
  onClose,
  entity,
  recordId,
  recordName,
  onLinked,
}: Omit<XeroLinkModalProps, 'open'>) {
  const meta = XERO_ENTITIES[entity];

  // Seeding with the record's own name puts the likely match on screen before
  // anyone types; the operator's job is then to confirm or reject, not to hunt.
  const [query, setQuery] = useState(recordName);
  const [matches, setMatches] = useState<XeroContactMatch[]>([]);
  /*
   * True from the first frame whenever the seeded name gives us something to
   * search for. `setLoading(true)` only happens after the debounce, so starting
   * false here would flash "no matches" for 250ms before the first request has
   * even been sent.
   */
  const [loading, setLoading] = useState(recordName.trim() !== '');
  const [searchError, setSearchError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<XeroContactMatch | null>(null);

  /*
   * Bumped to re-run the search without touching the term — the 409 path needs
   * exactly that, because the term is still right and only the world moved.
   */
  const [reloadKey, setReloadKey] = useState(0);

  const searchRef = useRef<HTMLInputElement>(null);

  const term = query.trim();

  useEffect(() => {
    // An emptied box searches for nothing rather than for everything. Clearing
    // the results here would be a needless render — the empty term is what the
    // list renders from, so stale matches are already unreachable.
    if (!term) return;

    const controller = new AbortController();
    const { signal } = controller;

    // 250ms, so typing a school name doesn't fire a scored search per keystroke
    // against all ~940 contacts.
    const timer = setTimeout(() => {
      setLoading(true);
      api
        .searchXeroContacts({ q: term, entity, id: recordId, limit: RESULT_LIMIT }, signal)
        .then((response) => {
          if (signal.aborted) return;
          /*
           * `compareMatches` and nothing else. Customer-before-confidence is
           * load-bearing (see its comment); re-sorting by score, name or
           * anything the column headers might suggest reintroduces exactly the
           * mis-link it exists to prevent.
           */
          const sorted = [...response.matches].sort(compareMatches);
          setMatches(sorted);
          setSearchError(null);
          // A candidate can vanish between searches; a stale selection would
          // otherwise submit an id that is no longer on screen.
          setSelectedId((current) =>
            current && sorted.some((m) => m.xeroId === current) ? current : null,
          );
        })
        .catch((caught: unknown) => {
          if (signal.aborted) return;
          setMatches([]);
          setSearchError(
            caught instanceof Error ? caught.message : 'Could not search Xero contacts.',
          );
        })
        .finally(() => {
          if (!signal.aborted) setLoading(false);
        });
    }, 250);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [term, entity, recordId, reloadKey]);

  const selected = useMemo(
    () => matches.find((match) => match.xeroId === selectedId) ?? null,
    [matches, selectedId],
  );

  function submitLink(match: XeroContactMatch, force: boolean) {
    setLinking(true);
    setLinkError(null);

    // `force` is sent only after the confirmation below. Sending it by default
    // would turn the server's 409 — the one guard against forking two records
    // onto one Xero contact — into a no-op.
    const body = force ? { xeroId: match.xeroId, force: true } : { xeroId: match.xeroId };

    api
      .linkXero(entity, recordId, body)
      .then(() => {
        setLinking(false);
        setConfirming(null);
        onLinked();
        onClose();
      })
      .catch((caught: unknown) => {
        setLinking(false);
        setConfirming(null);

        if (caught instanceof ApiError && caught.status === 409) {
          /*
           * Someone linked this candidate between our search and our submit.
           * Forcing past it here would fork the link on the strength of a
           * warning the operator never saw, so the list is refreshed and the
           * decision is put back to them with the new `takenBy` in view.
           */
          setSelectedId(null);
          setLinkError(
            'That Xero contact was claimed by another SEED EMERGE record while this list was open. ' +
              'The candidates have been refreshed — check it again before linking.',
          );
          setReloadKey((key) => key + 1);
          return;
        }

        /*
         * Rendered as a banner, never per field. A 403 body carries no
         * `details` at all (unlike a 422), and this dialog has one field
         * anyway — a message pinned to the search box would be read as "your
         * search term is wrong", which is the one thing it never means.
         */
        setLinkError(
          caught instanceof Error ? caught.message : 'Could not link this record to Xero.',
        );
      });
  }

  function onLinkClick() {
    if (!selected) return;
    // Forking is always a confirmation, never a silent allow.
    if (isForkingLink(selected)) {
      setConfirming(selected);
      return;
    }
    submitLink(selected, false);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Link to a Xero ${meta.xeroName.toLowerCase()}`}
      description={
        <>
          Attach <span className="font-medium text-fg-secondary">{recordName}</span> to an existing{' '}
          {meta.xeroName.toLowerCase()}. Nothing is written to Xero.
        </>
      }
      size="lg"
      dismissOnBackdrop={false}
      initialFocusRef={searchRef}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={linking}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onLinkClick}
            disabled={!selected}
            loading={linking && !confirming}
          >
            {selected && isForkingLink(selected) ? 'Link anyway…' : 'Link'}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        {/*
         * A real <form> so Enter re-runs the search rather than doing nothing —
         * and `stopPropagation` because this dialog is opened from inside other
         * forms. It is portalled to the body, but React propagates by the REACT
         * tree, so an un-stopped submit reaches the form behind and saves a
         * half-filled record. That exact bug has shipped from this repo before.
         */}
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setReloadKey((key) => key + 1);
          }}
        >
          <Field label="Search Xero contacts" hideLabel>
            {(fieldProps) => (
              <Input
                {...fieldProps}
                ref={searchRef}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by name or email…"
                autoComplete="off"
                leadingIcon={<SearchIcon />}
              />
            )}
          </Field>
        </form>

        {linkError && (
          <Notice tone="danger" role="alert">
            {linkError}
          </Notice>
        )}

        {/*
         * The list owns its scrolling so the search box and the footer stay put
         * while a long candidate list moves. `min-w-0` because a grid item
         * defaults to `min-width: auto` and a long vendor name would otherwise
         * widen the whole dialog instead of wrapping inside it.
         */}
        <div className="min-w-0 max-h-[22rem] overflow-y-auto overscroll-contain rounded-md border border-border">
          {/* The empty term is checked first: it outranks a `loading` flag left
              standing by the search that was in flight when the box was cleared. */}
          {!term ? (
            <EmptyState
              title="Type a name to search"
              description={`Searching Xero for the ${meta.xeroName.toLowerCase()} that matches this ${meta.label}.`}
            />
          ) : loading ? (
            <LoadingRows />
          ) : searchError ? (
            <Notice tone="danger" role="alert" className="m-3">
              {searchError}
            </Notice>
          ) : matches.length === 0 ? (
            <EmptyState
              title="No Xero contacts matched"
              description={
                /*
                 * With ~940 contacts in the tenant, "no matches" almost always
                 * means the term is wrong rather than the contact missing —
                 * Xero holds schools separately ("Boston University - Questrom
                 * School of Business"), so a shorter fragment usually finds it.
                 */
                'Try a shorter fragment of the name, or an email address.'
              }
            />
          ) : (
            <ul className="divide-y divide-border">
              {matches.map((match) => (
                <li key={match.xeroId}>
                  <MatchRow
                    match={match}
                    selected={match.xeroId === selectedId}
                    onSelect={() => {
                      setSelectedId(match.xeroId);
                      setLinkError(null);
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        onConfirm={() => confirming && submitLink(confirming, true)}
        title="Two records, one Xero contact"
        confirmLabel="Link anyway"
        tone="danger"
        loading={linking}
        description={
          confirming && (
            <>
              <span className="font-medium text-fg">{confirming.name}</span> is already linked to{' '}
              <span className="font-medium text-fg">{confirming.takenBy?.name}</span>. Linking{' '}
              <span className="font-medium text-fg">{recordName}</span> too means each will
              overwrite what the other sent.
            </>
          )
        }
      />
    </Modal>
  );
}

function MatchRow({
  match,
  selected,
  onSelect,
}: {
  match: XeroContactMatch;
  selected: boolean;
  onSelect: () => void;
}) {
  const band = confidenceBand(match.score);

  return (
    <button
      type="button"
      onClick={onSelect}
      // Archived contacts cannot be written to, so linking one would only ever
      // produce an error later. Shown rather than hidden: the id is still the
      // right id, and knowing it exists is what stops a duplicate being made.
      disabled={match.isArchived}
      aria-pressed={selected}
      className={cx(
        'flex w-full flex-col gap-1.5 px-3 py-2.5 text-left',
        'transition-colors duration-150 ease-out motion-reduce:transition-none',
        'disabled:cursor-not-allowed disabled:opacity-60',
        selected ? 'bg-surface-active' : 'hover:bg-surface-hover disabled:hover:bg-transparent',
      )}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <span className="min-w-0 break-words text-sm font-medium text-fg">{match.name}</span>
        {/*
         * The band words, never the raw score. A percentage invites someone to
         * read a fuzzy string comparison as a measurement — see `confidenceBand`.
         */}
        <Badge tone={bandTone(band)}>{bandLabel(band)}</Badge>
        {match.isArchived && <Badge tone="warning">Archived in Xero</Badge>}
      </div>

      <p className="min-w-0 break-words text-xs text-fg-muted">{orDash(match.email)}</p>

      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        {/*
         * The marker that separates a university from a taxi firm. Neutral, not
         * a warning: plenty of legitimate new customers have never been
         * invoiced. It just has to be visible before the click, not after.
         */}
        {!match.isLikelyCustomer && (
          <Badge tone="neutral" variant="outline">
            No invoices in Xero
          </Badge>
        )}
        {match.takenBy && <Badge tone="warning">Already linked to {match.takenBy.name}</Badge>}
      </div>

      <p className="min-w-0 break-words text-xs text-fg-subtle">{match.reason}</p>
    </button>
  );
}

function LoadingRows() {
  return (
    <ul className="divide-y divide-border" aria-busy="true">
      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <li key={index} className="flex flex-col gap-1.5 px-3 py-2.5">
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-40 max-w-[55%]" />
            <Skeleton className="h-4 w-20" />
          </div>
          <Skeleton className="h-3 w-52 max-w-[70%]" />
          <Skeleton className="h-3 w-32" />
        </li>
      ))}
    </ul>
  );
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}
